import { gql, useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'

import {
  Maybe,
  PaymentDetailFragmentDoc,
  PersonalInformationType,
  PreSaleLotteryDetailForGuestQuery,
  PreSaleLotteryDetailForGuestQueryVariables,
  PreSaleLotteryDetailFragmentDoc,
  PreSaleLotteryDetailQuery,
  PreSaleLotteryDetailQueryVariables,
  UserDeliveryDetailFragment,
  UserDeliveryDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import {
  getPrefrenceObject,
  removePrefernce,
  setPreference,
} from '@/core/native/storage'
import { useRootAuth } from '@/core/rootAuth'
import { decodeSkuId } from '@/core/sku'

import { Action, State } from './template'
import { ApplyPreSaleLotteryValue } from './template/LotteryDetailForm'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { router, routerPages } = useRouter<{
    preSaleId: string
    itemId: string
    quantity: string
    skuId: string
    clientId?: string
    token?: string
  }>()
  const rootAuthInfo = useRootAuth()
  /** 外部アプリのルート認証に失敗したかどうかの状態 */
  const [isRootAuthFailed, setIsRootAuthFailed] = useState<boolean>(false)

  const [choice, setChoice] = useState<State['choices']>()
  const { data, loading } = useQuery<
    PreSaleLotteryDetailQuery,
    PreSaleLotteryDetailQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.preSaleId,
      preSaleId: router.query.preSaleId,
      clientId: rootAuthInfo.clientId,
      token: rootAuthInfo.token,
    },
    skip: !user?.isSigned,
    onError: error => {
      setIsRootAuthFailed(
        error?.graphQLErrors?.some(
          error => error?.extensions?.code === 'FORBIDDEN'
        ) ?? false
      )
    },
  })

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    PreSaleLotteryDetailForGuestQuery,
    PreSaleLotteryDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: {
      id: router.query.preSaleId,
      clientId: rootAuthInfo.clientId,
      token: rootAuthInfo.token,
    },
    skip: !!user?.isSigned,
    onError: error => {
      setIsRootAuthFailed(
        error?.graphQLErrors?.some(
          error => error?.extensions?.code === 'FORBIDDEN'
        ) ?? false
      )
    },
  })

  useEffect(() => {
    ;(async () => {
      const v = await getPrefrenceObject<ApplyPreSaleLotteryValue>(
        'preSaleLotteryApplicationInput'
      )

      // 戻るボタンによる遷移の場合、購入内容を保持する
      if (v.preSaleId === router.query.preSaleId) {
        setChoice(
          v.choices.map((c, i) => ({
            itemId: c.itemId,
            skuId: c.skuId,
            count: v.purchaseCountChoices[i].count,
          }))
        )
      }
      // LocalStorageに保存したFormの値保持用の一次データを削除
      await removePrefernce('preSaleLotteryApplicationInput')
    })()
  }, [])

  const fees = data?.preSale?.items?.[0]?.fees
  const paymentFees = useMemo(
    () =>
      (fees?.collections || [])
        .concat(fees?.konbini ? [fees.konbini] : [])
        .concat(fees?.shipping ? [fees.shipping] : [])
        .filter(fee => !!fee)
        .map(fee => ({
          feeName: fee?.feeName,
          fee: fee?.fee ?? 0,
          per: fee?.per,
          feeType: fee?.feeType,
          formattedFee: fee?.formattedFee,
        })),
    [fees]
  )

  return {
    state: {
      isRootAuthFailed,
      preSale: data?.preSale ?? dataForGuest?.preSale,
      isAppliedUser:
        !!data?.preSaleLotteryApplicant?.id &&
        data.preSaleLotteryApplicant.status !== '60_canceled',
      paymentSource: data?.paymentSource,
      loading: loading || loadingForGuest,
      originFullNameValue: data?.user?.fullName ?? '',
      paymentFees,
      konbiniFee: fees?.konbini,
      choices:
        choice ??
        (router.query.itemId
          ? [
              {
                itemId: router.query.itemId,
                skuId: router.query.skuId && decodeSkuId(router.query.skuId),
                count: Number(router.query.quantity) ?? 1,
              },
            ]
          : undefined),
    },
    actions: {
      /** クレカ登録画面に遷移する */
      async onReadyPaymentMethod(values?: ApplyPreSaleLotteryValue) {
        if (values?.choices.length) {
          // 内部ストレージに保存し、遷移先で取り出す
          await setPreference('preSaleLotteryApplicationInput', values)
        }
        router.push(routerPages.PaymentMethodSetting)
      },
      async onSubmit(values: ApplyPreSaleLotteryValue | undefined) {
        if (!values) return

        // 内部ストレージに保存し、遷移先で取り出す
        await setPreference('preSaleLotteryApplicationInput', values)

        await analytics?.track('click_preSaleLotteryConfirm', {
          applicationKey: config.applicationKey,
          preSaleId: router.query.preSaleId,
          choiceCount: values?.choices.length,
          paymentMethod: values.paymentMethod,
        })

        if (
          needToRegisterUserInfo(
            data?.preSale.lottery?.personalInformationRequirements ?? [],
            data?.user
          )
        ) {
          return router.push(
            generatePath(routerPages.LotteryUserInfo, {
              preSaleId: router.query.preSaleId,
            })
          )
        }

        if (
          needToRegisterUserAddress(
            data?.preSale.lottery?.personalInformationRequirements ?? [],
            data?.user
          )
        ) {
          return router.push(
            generatePath(routerPages.LotteryDeliveryInfo, {
              preSaleId: router.query.preSaleId,
            })
          )
        }

        router.push(
          generatePath(routerPages.LotteryConfirmation, {
            preSaleId: router.query.preSaleId,
          })
        )
      },
    },
  }
})

const QUERY = gql`
  query PreSaleLotteryDetail(
    $id: ID!
    $preSaleId: String!
    $clientId: String
    $token: String
  ) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      ...PreSaleLotteryDetail
    }
    preSaleLotteryApplicant(preSaleId: $preSaleId) {
      id
      status
    }
    paymentSource {
      ...PaymentDetail
    }
    user {
      ...UserDeliveryDetail
    }
  }
  ${PreSaleLotteryDetailFragmentDoc}
  ${PaymentDetailFragmentDoc}
  ${UserDeliveryDetailFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query PreSaleLotteryDetailForGuest(
    $id: ID!
    $clientId: String
    $token: String
  ) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      ...PreSaleLotteryDetail
    }
  }
  ${PreSaleLotteryDetailFragmentDoc}
`

/**
 *
 * @param personalInformationRequirements
 * @param user
 */
export function needToRegisterUserInfo(
  personalInformationRequirements: Array<PersonalInformationType>,
  user?: Maybe<UserDeliveryDetailFragment>
): boolean {
  const requiredFullName =
    personalInformationRequirements.some(
      req => req === PersonalInformationType.FullName
    ) && !user?.fullName
  const requiredBirthday =
    personalInformationRequirements.some(
      req => req === PersonalInformationType.Birthday
    ) && !user?.birthday
  const requiredGender =
    personalInformationRequirements.some(
      req => req === PersonalInformationType.Gender
    ) && !user?.gender
  const requiredPhoneNumber =
    personalInformationRequirements.some(
      req => req === PersonalInformationType.PhoneNumber
    ) &&
    (!user?.phoneNumber || !user?.isPhoneNumberVerified)

  return (
    requiredFullName ||
    requiredBirthday ||
    requiredGender ||
    requiredPhoneNumber
  )
}

/**
 *
 * @param personalInformationRequirements
 * @param user
 */
export function needToRegisterUserAddress(
  personalInformationRequirements: Array<PersonalInformationType>,
  user?: Maybe<UserDeliveryDetailFragment>
): boolean {
  return (
    personalInformationRequirements?.some(
      req => req === PersonalInformationType.Address
    ) &&
    (!user?.address?.postalCode ||
      !user?.address?.prefecture ||
      !user?.address?.city ||
      !user?.address?.line1)
  )
}
